import React, { useState, useRef, useEffect } from "react";
import { Clock, PhoneForwarded, ChevronDown, Check, X, Search } from "lucide-react";
import Dropdown from "../Dropdown/Dropdown.js";

function MultiSelectDropdown({ options = [], selectedValues = [], onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleOption = (val) => {
    let nextValues;
    if (selectedValues.includes(val)) {
      nextValues = selectedValues.filter(v => v !== val);
    } else {
      nextValues = [...selectedValues, val];
    }
    onChange(nextValues);
  };

  const filteredOptions = options.filter(opt => 
    (opt.label || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", marginBottom: "20px" }}>
      <label className="field-label" style={{ marginBottom: "6px", display: "block" }}>{placeholder}</label>
      
      {/* Box display with selected values */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: "42px",
          padding: "6px 12px",
          borderRadius: "8px",
          border: isOpen ? "2px solid #2563eb" : "1px solid var(--gray-200, #cbd5e1)",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "6px",
          cursor: "pointer",
          boxShadow: isOpen ? "0 0 0 3px rgba(37, 99, 235, 0.1)" : "none",
          transition: "all 0.2s ease",
          userSelect: "none"
        }}
      >
        {selectedValues.length === 0 ? (
          <span style={{ color: "var(--slate-400, #94a3b8)", fontSize: "14px" }}>Select Disposition(s)</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {selectedValues.map(val => {
              const option = options.find(o => o.value === val);
              const label = option ? option.label : val;
              return (
                <div
                  key={val}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "#eff6ff",
                    color: "#2563eb",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: "4px",
                    border: "1px solid #bfdbfe",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleOption(val);
                  }}
                >
                  <span>{label}</span>
                  <X size={12} style={{ cursor: "pointer", display: "inline-flex" }} />
                </div>
              );
            })}
          </div>
        )}
        <ChevronDown size={16} style={{ color: "var(--slate-500, #64748b)", transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s ease", marginLeft: "auto" }} />
      </div>

      {/* Floating Option list list */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            zIndex: 100,
            overflow: "hidden",
            padding: "8px"
          }}
        >
          {/* Search box inside option list */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px", 
            background: "#f1f5f9", /* Slate gray background */
            border: "1px solid #cbd5e1", 
            borderRadius: "6px", 
            padding: "6px 10px", 
            marginBottom: "8px" 
          }}>
            <Search size={14} style={{ color: "#64748b", flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search dispositions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                border: "none",
                borderWidth: 0,
                background: "transparent",
                backgroundColor: "transparent",
                boxShadow: "none",
                outline: "none",
                fontSize: "13.5px",
                width: "100%",
                height: "auto",
                padding: "2px 0",
                color: "#1e293b",
                borderRadius: "0px"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Option list items scroll list */}
          <div style={{ maxHeight: "180px", overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: "8px 12px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>No options found</div>
            ) : (
              filteredOptions.map(opt => {
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleToggleOption(opt.value)}
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: isSelected ? "#2563eb" : "#334155",
                      background: isSelected ? "#f1f5f9" : "transparent",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "2px",
                      transition: "background 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f1f5f9"}
                    onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? "#f1f5f9" : "transparent"}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <Check size={14} style={{ color: "#2563eb" }} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CallConfigTab({
  selectedAgent,
  handleInputChange,
  dialerOptions = [],
  dispositionOptions = [],
  formErrors = {},
}) {
  return (
    <div className="tab-panel-content">
      {/* schedules/timeouts section */}
      <div className="config-section-simple">
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Clock size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Schedules & Timeout Restrictions</span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px" }}>
            Telephony Dialer <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </label>
          <Dropdown
            options={dialerOptions}
            value={selectedAgent?.dialer}
            onChange={(e) => handleInputChange("dialer", e.target.value)}
            placeholder="Telephony Dialer"
          />
          {formErrors.dialer && (
            <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
              {formErrors.dialer}
            </div>
          )}
        </div>

        <MultiSelectDropdown
          options={dispositionOptions}
          selectedValues={selectedAgent?.dispositions || []}
          onChange={(values) => handleInputChange("dispositions", values)}
          placeholder="Select Call Dispositions"
        />

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", flexDirection: "row" }}>
          <div style={{ flex: 1, minWidth: "220px" }}>
            <label className="field-label">
              Daily Operations Start Time <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" className="field-input-textarea" style={{ height: 42, textAlign: "center" }} value={selectedAgent?.start_hour || ""} onChange={(e) => handleInputChange("start_hour", e.target.value)} />
              <input type="text" className="field-input-textarea" style={{ height: 42, textAlign: "center" }} value={selectedAgent?.start_minute || ""} onChange={(e) => handleInputChange("start_minute", e.target.value)} />
              <select
                className="field-input-textarea"
                style={{ height: 42, width: 90, cursor: "pointer", padding: "0 10px", fontWeight: "600" }}
                value={selectedAgent?.start_am_pm || "AM"}
                onChange={(e) => handleInputChange("start_am_pm", e.target.value)}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: "220px" }}>
            <label className="field-label">
              Daily Operations End Time <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" className="field-input-textarea" style={{ height: 42, textAlign: "center" }} value={selectedAgent?.end_hour || ""} onChange={(e) => handleInputChange("end_hour", e.target.value)} />
              <input type="text" className="field-input-textarea" style={{ height: 42, textAlign: "center" }} value={selectedAgent?.end_minute || ""} onChange={(e) => handleInputChange("end_minute", e.target.value)} />
              <select
                className="field-input-textarea"
                style={{ height: 42, width: 90, cursor: "pointer", padding: "0 10px", fontWeight: "600" }}
                value={selectedAgent?.end_am_pm || "PM"}
                onChange={(e) => handleInputChange("end_am_pm", e.target.value)}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* human escalation section */}
      <div className="config-section-simple" style={{ marginTop: 32 }}>
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <PhoneForwarded size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Call Transfer (Human Escalation)</span>
        </div>

        {/* Elegant Slider Toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <span className="field-label" style={{ margin: 0, fontWeight: 700, color: "var(--slate-800)" }}>
            Enable Call Transfer <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
          </span>
          <label style={{ position: "relative", display: "inline-block", width: "44px", height: "24px", cursor: "pointer" }}>
            <input
              type="checkbox"
              style={{ opacity: 0, width: 0, height: 0 }}
              checked={selectedAgent?.transfer_enabled ?? false}
              onChange={(e) => handleInputChange("transfer_enabled", e.target.checked)}
            />
            <span style={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: selectedAgent?.transfer_enabled ? "#2563eb" : "#cbd5e1",
              borderRadius: "24px",
              transition: "0.3s",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)"
            }}>
              <span style={{
                position: "absolute",
                left: "2px",
                bottom: "2px",
                height: "20px",
                width: "20px",
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                transition: "0.3s",
                transform: selectedAgent?.transfer_enabled ? "translateX(20px)" : "translateX(0px)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
              }} />
            </span>
          </label>
        </div>

        {/* Conditionally Show Transfer Destination Number input and Threshold */}
        {(selectedAgent?.transfer_enabled ?? false) && (
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", flexDirection: "row", marginTop: 12 }}>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label className="field-label">
                Transfer Destination Number <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
              </label>
              <input
                type="tel"
                className="field-input-textarea"
                style={{ 
                  height: 42,
                  border: formErrors.transfer_number ? "1px solid #ef4444" : undefined,
                  boxShadow: formErrors.transfer_number ? "0 0 0 3px rgba(239, 68, 68, 0.15)" : undefined
                }}
                placeholder="+91 XXXXX XXXXX"
                value={selectedAgent?.transfer_number || ""}
                onChange={(e) => handleInputChange("transfer_number", e.target.value)}
              />
              {formErrors.transfer_number ? (
                <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
                  {formErrors.transfer_number}
                </div>
              ) : (
                <div className="field-helper" style={{ marginTop: 6 }}>Define the number for destination where calls will be forwarded.</div>
              )}
            </div>

            <div style={{ flex: 1, minWidth: "220px" }}>
              <div className="slider-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span className="field-label" style={{ margin: 0 }}>
                  Call Transfer Threshold <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
                </span>
                <span className="slider-val" style={{ color: "var(--secondary, #e63946)", fontWeight: 700 }}>{selectedAgent?.transfer_threshold ?? 0.7}</span>
              </div>
              <input
                type="range"
                min={0.1}
                max={1.0}
                step={0.05}
                className="custom-range-slider"
                style={{ marginTop: 10 }}
                value={selectedAgent?.transfer_threshold ?? 0.7}
                onChange={(e) => handleInputChange("transfer_threshold", Number(e.target.value))}
              />
              <div className="field-helper" style={{ marginTop: 16 }}>Set confidence score/trigger threshold for executing human escalation.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
